import shuffle from 'lodash-es/shuffle';

import { valuesToPercentage } from '../object'

export function getScoreFromArrays(userAnswers, points) {
  return userAnswers.filter((ans) => points.includes(ans)).length;
}

export function getPointByUserAnswer({ points, userAnswer, maxPossibleScore }) {
  if (points === 'userAnswer') {
    return userAnswer;
  }
  const normalizedPoints =
    typeof points === 'number'
      ? {
        positive: points,
        negative: points * -1,
      }
      : points || {};
  let keyForNumericValue;
  if (typeof userAnswer === 'number') {
    keyForNumericValue =
      userAnswer !== 0 && (userAnswer > 0 ? 'positive' : 'negative');
  }
  let score;
  let shouldDivideScore = false;
  if (Array.isArray(userAnswer) && Array.isArray(points)) {
    score = getScoreFromArrays(userAnswer, points);
  } else if (normalizedPoints[userAnswer]) {
    score = normalizedPoints[userAnswer];
  } else if (keyForNumericValue && normalizedPoints[keyForNumericValue]) {
    shouldDivideScore = true;
    score = normalizedPoints[keyForNumericValue];
  }

  score = score || 0;
  if (
    score &&
    typeof userAnswer === 'number' &&
    userAnswer &&
    maxPossibleScore &&
    shouldDivideScore
  ) {
    score = (score / maxPossibleScore) * Math.abs(userAnswer);
  }
  return score;
}

export function findRelatedAnswers({ group, answers, currentKey }) {
  return Object.entries(answers)
    .filter(([key, value]) => value.group === group && key !== currentKey)
    .map(([key, value]) => ({
      ...value,
      key,
    }));
}

export function mirrorPointValue(value) {
  if (!value || typeof value !== 'object') {
    return value;
  }
  const keys = Object.keys(value);
  return Object.entries(value).reduce((acc, [key, v], index) => {
    let newKey = key;
    if (!Number.isNaN(+key)) {
      newKey = key === 0 ? 0 : key * -1;
    } else if (key === 'positive') {
      newKey = 'negative';
    } else if (key === 'negative') {
      newKey = 'positive';
    } else if (/[a-z]{1}/.test(key)) {
      newKey = keys[keys.length - 1 - index];
    }
    if (newKey != null) {
      acc[newKey] = v;
    }
    return acc;
  }, {});
}

export function getInitialAnswers(questions) {
  return questions.reduce((acc, question) => {
    if (question.initialValue) {
      acc[question.id] = question.initialValue;
    }
    return acc;
  }, {});
}

export function shouldSkipQuestion(question) {
  return question.type === 'text' || question.enabled === false;
}
export function shouldSkipQuestionProgress(question) {
  return question.skipProgress || shouldSkipQuestion(question);
}
export function shouldSkipQuestionValidation(question) {
  return (
    question.optional ||
    shouldSkipQuestion(question)
  );
}
export function resolveAnswerPoints(question) {
  if (!question?.points) {
    return {};
  }
  const defaultRelatedPoints = question.related?.[0]?.points;
  if (question.points === 'mirror') {
    if (!defaultRelatedPoints) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(defaultRelatedPoints).map(([key, value]) => [
        key,
        mirrorPointValue(value) || {},
      ])
    );
  }

  return Object.fromEntries(
    Object.entries(question.points).map(([qId, value]) => {
      const normalizedValue =
        value === 'mirror'
          ? mirrorPointValue(defaultRelatedPoints?.[qId])
          : value;
      return [qId, normalizedValue || {}];
    })
  );
}

export function getStepsFromQuestions(questions, { randomize } = {}) {
  let steps = Array.from(new Set(questions.map((q) => q.group)));
  if (randomize) {
    steps = shuffle(steps);
    if (randomize.persist) {
      Object.entries(randomize.persist).forEach(([groupKey, newIndex]) => {
        const currentIndex = steps.findIndex(
          (shuffledGroup) => shuffledGroup === groupKey
        );
        [steps[newIndex], steps[currentIndex]] = [
          steps[currentIndex],
          steps[newIndex],
        ];
      });
    }
  }
  return steps;
}

const scaleForTests = {
  2: 4,
  1: 3,
  0: 2,
  '-1': 1,
  '-2': 0,
};
const revertScaleForTests = {
  2: 0,
  1: 1,
  0: 2,
  '-1': 3,
  '-2': 4,
};

export const getScales = (ids, { scale, revert = false } = {}) =>
  Object.fromEntries(
    ids.map((id) => [
      id,
      scale || (revert ? revertScaleForTests : scaleForTests),
    ])
  );

const rangesInterpretations = {
  '0:1': 'low',
  '-1:1': 'high',
  '0:2': 'low',
  '1:2': 'medium',
  '-1:2': 'high',
  '0:3': 'extremelyLow',
  '1:3': 'low',
  '2:3': 'medium',
  '-1:3': 'high',
  '0:4': 'extremelyLow',
  '1:4': 'low',
  '2:4': 'medium',
  '3:4': 'high',
  '-1:4': 'extremelyHigh',
};
export const percentageToRate = (percentage, { ranges }) => {
  const foundIndex = ranges.findIndex((range) => percentage < range);
  return (
    rangesInterpretations[foundIndex] ||
      rangesInterpretations[`${foundIndex}:${ranges.length}`] ||
      ranges[ranges.length - 1]
  );
};
export const valuesToRate = (
  result,
  { max = 1, round, ranges = [30, 60] } = {}
) => {
  const percentages = valuesToPercentage(result, { max, round });
  return Object.fromEntries(
    Object.entries(result).map(([key]) => {
      const rate = percentageToRate(percentages[key], { ranges });
      return [key, rate];
    })
  );
};